<template>
  <div
    class="range-wrapper"
    :style="{
      '--thumb-min': min_percentage,
      '--thumb-max': max_percentage,
    }"
  >
    <div v-if="items" class="range-slider">
      <div v-if="inputs" class="range-number-input">
        <input
          type="number"
          :min="min_angle"
          :max="max_angle"
          step="1"
          :value="slider_min"
          @change="(e) => handle_slider_min_input(e, false)"
        />
        <input
          type="number"
          :min="min_angle"
          :max="max_angle"
          step="1"
          :value="slider_max"
          @change="(e) => handle_slider_max_input(e, false)"
        />
      </div>

      <div v-if="items.length > 1" class="range-bars">
        <div
          v-for="(item, index) in items"
          :key="index"
          v-b-popover.hover.top="format_value(item.min)"
          class="range-bar-wrapper"
          :style="{ height: get_height(item) }"
        >
          <div
            :class="is_bar_selected(index) ? 'range-bar selected' : 'range-bar'"
          ></div>
        </div>
      </div>

      <div class="range-slider-input" @click.prevent="handle_click">
        <input
          ref="min"
          type="range"
          :min="min_angle"
          :max="max_angle"
          :value="slider_min"
          step="1"
          @input.prevent="(e) => handle_slider_min_input(e, true)"
          @change.prevent="(e) => handle_slider_min_input(e, false)"
        />

        <input
          ref="max"
          type="range"
          :min="min_angle"
          :max="max_angle"
          :value="slider_max"
          step="1"
          @input.prevent="(e) => handle_slider_max_input(e, true)"
          @change.prevent="(e) => handle_slider_max_input(e, false)"
        />
      </div>
    </div>

    <div v-if="vb_options.debug">
      <br /><br />
      PROPS<br />
      min: {{ vb_options.min }}<br />
      max: {{ vb_options.max }}<br />
      items: {{ items.length }}<br />
      min_angle: {{ vb_options.min_angle }}<br />
      max_angle: {{ vb_options.max_angle }}<br />
      <hr />
      DATA:<br />
      min_angle: {{ min_angle }}<br />
      max_angle: {{ max_angle }}<br />
      slider_min_live: {{ slider_min_live }}<br />
      slider_max_live: {{ slider_max_live }}<br />
      slider_min: {{ slider_min }}<br />
      slider_max: {{ slider_max }}<br />
      items: {{ items.length }}<br />
    </div>
  </div>
</template>

<script>
  import {
    number,
    subtract,
    sum,
    divide,
    multiply,
    floor,
    ceil,
    abs,
    min,
    max,
    round,
    add,
    format,
  } from 'mathjs';
  export default {
    name: 'vb-range-slider',
    props: {
      vb_options: {
        type: Object,
        default: () => ({
          min: 0, // default min pre-selezionato
          max: 100, // default max pre-selezionato
          items: [],
          inputs: true,
          allow_click: true,
          min_angle: 0, // bound inferiore intervallo
          max_angle: 0, // bound superiore intervallo
          debug: false,
          threshold: 0,
        }),
      },
    },
    data() {
      return {
        slider_min: 0,
        slider_max: 0,
        slider_min_live: 0,
        slider_max_live: 0,
      };
    },
    computed: {
      items() {
        return this.vb_options.items;
      },
      inputs() {
        return this.vb_options.inputs;
      },
      min_angle() {
        let bound = this.vb_options.min;
        if (this.vb_options.min_angle) bound = this.vb_options.min_angle;
        return ceil(number(bound));
      },
      max_angle() {
        let bound = this.vb_options.max;
        if (this.vb_options.max_angle) bound = this.vb_options.max_angle;
        return floor(number(bound));
      },
      min_percentage() {
        return `${this.value_to_percentage(this.slider_min_live)}%`;
      },
      max_percentage() {
        return `${this.value_to_percentage(this.slider_max_live)}%`;
      },
    },
    watch: {
      'vb_options.min': 'sync_selection',
      'vb_options.max': 'sync_selection',
      'vb_options.min_angle': 'sync_selection',
      'vb_options.max_angle': 'sync_selection',
    },
    created() {
      this.sync_selection();
    },
    methods: {
      format_value(num) {
        if (!num) return num;
        const n = floor(number(num));
        const formatted = format(n, { notation: 'fixed', precision: 0 });
        return `${formatted.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€`;
      },
      clamp_to_angles(value, fallback) {
        if (value === undefined || value === null) return fallback;
        const candidate = number(value);
        if (candidate < this.min_angle) return this.min_angle;
        if (candidate > this.max_angle) return this.max_angle;
        return candidate;
      },
      value_to_percentage(value) {
        const range = subtract(this.max_angle, this.min_angle);
        if (range <= 0) return 0;
        let percentage = multiply(
          divide(subtract(value, this.min_angle), range),
          100
        );
        if (percentage < 0) percentage = 0;
        if (percentage > 100) percentage = 100;
        return round(percentage, 2);
      },
      sync_selection() {
        let next_min = this.clamp_to_angles(
          this.vb_options.min,
          this.min_angle
        );
        let next_max = this.clamp_to_angles(
          this.vb_options.max,
          this.max_angle
        );
        if (next_min > next_max) {
          next_min = this.min_angle;
          next_max = this.max_angle;
        }
        this.slider_min = next_min;
        this.slider_min_live = next_min;
        this.slider_max = next_max;
        this.slider_max_live = next_max;
      },
      is_bar_selected(index) {
        const total_bars = this.items.length;
        const range = subtract(this.max_angle, this.min_angle);

        const bar_start = sum(
          this.min_angle,
          multiply(divide(index, total_bars), range)
        );
        const bar_end = sum(
          this.min_angle,
          multiply(divide(add(index, 1), total_bars), range)
        );

        return (
          bar_start <= this.slider_max_live && bar_end >= this.slider_min_live
        );
      },
      get_height(item) {
        const min_height = 10;
        const counts = this.items.map((i) => i.count);
        const min_count = min(...counts);
        const max_count = max(...counts);

        if (max_count === min_count) return '100%';

        const normalized = divide(
          subtract(item.count, min_count),
          subtract(max_count, min_count)
        );

        if (normalized === 0) return '0%';

        const percent = sum(
          min_height,
          multiply(normalized, subtract(100, min_height))
        );

        return `${percent}%`;
      },
      handle_slider_min_input(event, dragging = false) {
        let val = number(event.target.value);
        if (val < this.min_angle) val = this.min_angle;
        if (val > this.slider_max) val = this.slider_max;
        event.target.value = val;
        this.slider_min = val;
        this.slider_min_live = val;
        this.$emit('change', {
          min: this.slider_min,
          dragging,
        });
      },
      handle_slider_max_input(event, dragging = false) {
        let val = number(event.target.value);
        if (val > this.max_angle) val = this.max_angle;
        if (val < this.slider_min) val = this.slider_min;
        const first_bucket = this.items.filter(
          (i) => i.min >= this.slider_min
        )[0];
        if (first_bucket && val < number(first_bucket.max)) {
          val = number(first_bucket.max);
        }
        event.target.value = val;
        this.slider_max = val;
        this.slider_max_live = val;
        this.$emit('change', {
          max: this.slider_max,
          dragging,
        });
      },
      handle_click(event) {
        if (this.vb_options.allow_click === false) return;
        event.preventDefault();
        if (event.target.type === 'range') return;
        const input = event.target;
        const rect = input.getBoundingClientRect();
        const click_x = subtract(event.clientX, rect.left);
        const percent = divide(click_x, rect.width);
        const new_value = round(
          sum(
            this.min_angle,
            multiply(percent, subtract(this.max_angle, this.min_angle))
          )
        );
        if (new_value < this.slider_min) {
          this.slider_min = new_value;
          this.slider_min_live = new_value;
          this.$emit('change', {
            min: this.slider_min,
            dragging: false,
          });
          return;
        }
        if (new_value > this.slider_max) {
          this.slider_max = new_value;
          this.slider_max_live = new_value;
          this.$emit('change', {
            max: this.slider_max,
            dragging: false,
          });
          return;
        }
        const slider_min_delta = abs(subtract(this.slider_min, new_value));
        const slider_max_delta = abs(subtract(this.slider_max, new_value));
        if (slider_min_delta < slider_max_delta) {
          this.slider_min = new_value;
          this.slider_min_live = new_value;
          this.$emit('change', {
            min: this.slider_min,
            dragging: false,
          });
          return;
        }

        this.slider_max = new_value;
        this.slider_max_live = new_value;
        this.$emit('change', {
          max: this.slider_max,
          dragging: false,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .range {
    &-slider {
      width: 300px;
      margin: auto;
      text-align: center;
      position: relative;
      min-height: 60px;
      //height: 6em;

      input[type='range'] {
        position: absolute;
        left: 0;
        bottom: 0;
        pointer-events: none; /* disabilita tutto */
        &::-webkit-slider-thumb {
          pointer-events: auto; /* abilita solo il drag */
        }
        &::-moz-range-thumb {
          pointer-events: auto; /* abilita solo il drag */
        }
        background-color: transparent;
        @-moz-document url-prefix() {
          /* Firefox only (autoprefixer can't parse @supports selector()) */
          bottom: -11px;
        }
      }

      input[type='number'] {
        border: 1px solid #ddd;
        text-align: center;
        -moz-appearance: textfield;
        appearance: textfield;
      }

      input[type='number']::-webkit-outer-spin-button,
      input[type='number']::-webkit-inner-spin-button {
        -webkit-appearance: none;
      }

      input[type='number']:invalid,
      input[type='number']:out-of-range {
        border: 2px solid var(--secondary);
      }

      input[type='range'] {
        -webkit-appearance: none;
        appearance: none;
        width: 100%;
      }

      input[type='range']:focus {
        outline: none;
      }

      input[type='range']:focus::-webkit-slider-runnable-track {
        background: linear-gradient(
          90deg,
          #ddd var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          #ddd var(--thumb-max)
        );
      }

      input[type='range']:focus::-moz-range-track {
        background: linear-gradient(
          90deg,
          transparent var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          transparent var(--thumb-max)
        );
      }

      input[type='range']::-webkit-slider-runnable-track {
        width: 100%;
        height: 3px;
        cursor: pointer;
        background: linear-gradient(
          90deg,
          #ddd var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          #ddd var(--thumb-max)
        );
        border-radius: 1px;
        box-shadow: none;
        border: 0;
      }

      input[type='range']::-moz-range-track {
        width: 100%;
        height: 3px;
        cursor: pointer;
        background: linear-gradient(
          90deg,
          transparent var(--thumb-min),
          var(--info) var(--thumb-min),
          var(--info) var(--thumb-max),
          transparent var(--thumb-max)
        );
        border-radius: 1px;
        box-shadow: none;
        border: 0;
      }

      input[type='range']::-webkit-slider-thumb {
        z-index: 2;
        position: relative;
        box-shadow: 0px 0px 0px #000;
        //border: 1px solid var(--info);
        height: 15px;
        width: 15px;
        border-radius: 25px;
        background: var(--info);
        cursor: pointer;
        -webkit-appearance: none;
        margin-top: -6px;
      }

      input[type='range']::-moz-range-thumb {
        z-index: 2;
        position: relative;
        box-shadow: 0px 0px 0px #000;
        height: 15px;
        width: 15px;
        border-radius: 25px;
        background: var(--info);
        cursor: pointer;
        border: 0; // Firefox centra il thumb da solo, niente margin-top
      }
    }

    &-number-input {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
    }

    &-slider-input {
      //background-color: red;
      height: 100%;
      width: 100%;
      position: absolute;
      bottom: 0;
      @-moz-document url-prefix() {
        /* Firefox only (autoprefixer can't parse @supports selector()) */
        border-bottom: 3px solid #ddd;
      }
    }

    &-wrapper {
      position: relative;
    }

    &-bars {
      height: 80px;
      display: flex;
      align-items: flex-end;
      padding-left: 6px;
      padding-right: 6px;
    }

    &-bar-wrapper {
      position: relative;
      flex: 1;
      margin: 0 1px 0 0;
      background-color: #ddd;
      margin-bottom: 3px;
      @-moz-document url-prefix() {
        /* Firefox only (autoprefixer can't parse @supports selector()) */
        margin-bottom: 0px;
      }
    }

    &-bar {
      height: 100%;
      width: 100%;
      position: relative;
      &.selected {
        background-color: #bbb;
      }
    }
  }
</style>
